import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";

// ================== FORMAT HELPERS ==================

function formatDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDestination(dest) {
  if (dest === "usa") return "États-Unis (ESTA)";
  if (dest === "canada") return "Canada (eTA)";
  if (dest === "australie") return "Australie (ETA)";
  return "Non précisée";
}

// ================== STATUS ==================

function getStatusMeta(status) {
  if (status === "validee") {
    return {
      label: "Validée",
      badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    };
  }
  if (status === "annulee") {
    return {
      label: "Annulée",
      badgeClass: "bg-rose-50 text-rose-700 border border-rose-200",
    };
  }
  if (status === "payé") {
    return {
      label: "En attente (payé)",
      badgeClass: "bg-amber-50 text-amber-700 border border-amber-200",
    };
  }
  return {
    label: "En attente (non payée)",
    badgeClass: "bg-slate-50 text-slate-700 border border-slate-200",
  };
}

function getDossierStatusLabel(status) {
  if (status === "validee") return "Validée";
  if (status === "annulee") return "Annulée";
  if (status === "payé") return "En attente (payé)";
  return "En attente (non payée)";
}

function getPaymentStatusLabel(status) {
  if (status === "payé" || status === "validee" || status === "annulee") {
    return "Payé";
  }
  return "Non payé";
}

// ================== COMPONENT ==================

export default function Admin({ onBack }) {
  const [loggedIn, setLoggedIn] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const [demandes, setDemandes] = useState([]);
  const [filterStatus, setFilterStatus] = useState("toutes");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  // ================== LOGIN ==================

  const handleLogin = async (e) => {
    e.preventDefault();

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error("Erreur login:", error);
      setLoginError("Identifiants incorrects");
    } else {
      setLoginError("");
      setLoggedIn(true);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn("Erreur signOut (non bloquante):", e);
    }
    setLoggedIn(false);
    setPassword("");
    setEmail("");
    setDemandes([]);
    setSelectedId(null);
  };

  // ================== FETCH SUPABASE DIRECT ==================

  useEffect(() => {
    if (!loggedIn) return;

    const fetchDemandes = async () => {
      try {
        const { data, error } = await supabase
          .from("demandes")
          .select("*")
          .order("created_at", { ascending: false });

        if (error) {
          console.error("ERROR SUPABASE 👉", error);
          return;
        }

        const rows = Array.isArray(data) ? data : [];
        setDemandes(rows);
        setSelectedId(rows[0]?.id || null);
      } catch (e) {
        console.error("Erreur réseau fetchDemandes :", e);
      }
    };

    fetchDemandes();
  }, [loggedIn]);

  // ================== UPDATE STATUS (SUPABASE DIRECT) ==================

  const updateStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from("demandes")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) {
        console.error("Erreur update :", error);
        return;
      }

      setDemandes((prev) =>
        prev.map((d) => (d.id === id ? { ...d, status: newStatus } : d))
      );
    } catch (e) {
      console.error("Erreur réseau updateStatus :", e);
    }
  };

  // ================== FILTER HELPERS ==================

  const isPaidWaiting = (d) => d.status === "payé";
  const isValidee = (d) => d.status === "validee";
  const isAnnulee = (d) => d.status === "annulee";

  const filteredDemandes = demandes
    .filter((d) => {
      if (filterStatus === "toutes") return true;
      if (filterStatus === "en_attente") return isPaidWaiting(d);
      if (filterStatus === "validee") return isValidee(d);
      if (filterStatus === "annulee") return isAnnulee(d);
      return true;
    })
    .filter((d) => {
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();
      const data = d.data || {};
      return (
        String(d.id).toLowerCase().includes(q) ||
        (data.email || "").toLowerCase().includes(q) ||
        (data.nom || "").toLowerCase().includes(q) ||
        (data.prenoms || "").toLowerCase().includes(q)
      );
    });

  const selectedDemande =
    filteredDemandes.find((d) => d.id === selectedId) ||
    filteredDemandes[0] ||
    null;

  // ================== STATS ==================

  const total = demandes.length;
  const totalEnAttente = demandes.filter(isPaidWaiting).length;
  const totalValidees = demandes.filter(isValidee).length;
  const totalAnnulees = demandes.filter(isAnnulee).length;

  // ================== LOGIN SCREEN ==================

  if (!loggedIn) {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm border border-slate-100">
          <h1 className="text-xl font-bold text-indigo-800 mb-1 text-center">
            Admin VisagovAssist
          </h1>
          <p className="text-xs text-slate-500 mb-5 text-center">
            Accès réservé — merci d’entrer le mot de passe.
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Email admin
              </label>
              <input
                type="email"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Mot de passe
              </label>
              <input
                type="password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {loginError && (
                <p className="mt-1 text-[11px] text-red-500">{loginError}</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2.5 rounded-lg"
            >
              Se connecter
            </button>

            <button
              type="button"
              onClick={onBack}
              className="w-full text-xs mt-1 text-slate-500 hover:text-slate-700"
            >
              ⬅ Retour au site
            </button>
          </form>
        </div>
      </main>
    );
  }

  // ================== UI ADMIN ==================

  return (
    <main className="min-h-screen bg-slate-50 text-slate-800">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-base md:text-lg font-bold text-indigo-800">
              Admin — VisagovAssist
            </h1>
            <p className="text-[11px] text-slate-500">
              Gestion des demandes (en attente / validées / annulées)
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 hover:border-indigo-500 hover:text-indigo-700"
            >
              ⬅ Retour au site
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="text-xs px-3 py-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-900"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 md:px-6 py-5 md:py-7">
        {/* ================== STATS ================== */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <StatCard label="Total demandes" value={total} />
          <StatCard
            label="En attente (payées)"
            value={totalEnAttente}
            tone="amber"
          />
          <StatCard label="Validées" value={totalValidees} tone="emerald" />
          <StatCard label="Annulées" value={totalAnnulees} tone="rose" />
        </div>

        {/* ================== COLONNES ================== */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr,1.5fr] gap-6">
          {/* ========== COLONNE GAUCHE : LISTE ========= */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 flex flex-col h-[70vh]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
              <div>
                <h2 className="text-sm font-semibold text-slate-800">
                  Liste des demandes
                </h2>
                <p className="text-[11px] text-slate-500">
                  {filteredDemandes.length} résultat(s) affiché(s)
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
                <input
                  type="text"
                  placeholder="Rechercher (nom, email, ID...)"
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-xs md:text-sm w-full md:w-48"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                <div className="flex rounded-lg border border-slate-200 overflow-hidden text-[11px]">
                  {[
                    { key: "toutes", label: "Toutes" },
                    { key: "en_attente", label: "En attente (payées)" },
                    { key: "validee", label: "Validées" },
                    { key: "annulee", label: "Annulées" },
                  ].map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFilterStatus(f.key)}
                      className={`px-2.5 py-1 ${
                        filterStatus === f.key
                          ? "bg-indigo-600 text-white"
                          : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ================== TABLE ================== */}
            <div className="border border-slate-100 rounded-lg overflow-hidden flex-1 flex flex-col">
              <div className="hidden md:grid grid-cols-[1.4fr,1fr,1fr,0.8fr] text-[11px] bg-slate-50 border-b border-slate-100 px-3 py-2 text-slate-500">
                <span>Demandeur</span>
                <span>Destination</span>
                <span>Créée le</span>
                <span className="text-right">Statut</span>
              </div>

              <div className="flex-1 overflow-y-auto">
                {filteredDemandes.length === 0 ? (
                  <div className="p-4 text-center text-xs text-slate-500">
                    Aucune demande trouvée.
                  </div>
                ) : (
                  filteredDemandes.map((d) => {
                    const meta = getStatusMeta(d.status);
                    const data = d.data || {};
                    const isSelected =
                      selectedDemande && d.id === selectedDemande.id;

                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setSelectedId(d.id)}
                        className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-indigo-50/40 text-xs md:text-[11px] ${
                          isSelected ? "bg-indigo-50/60" : "bg-white"
                        }`}
                      >
                        <div className="grid md:grid-cols-[1.4fr,1fr,1fr,0.8fr] gap-1 md:gap-2 items-center">
                          <div>
                            <p className="font-semibold text-slate-800 truncate">
                              {data.prenoms || data.nom
                                ? `${data.prenoms || ""} ${data.nom || ""}`.trim()
                                : "Nom non renseigné"}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate">
                              {data.email || "Email non renseigné"}
                            </p>
                            <p className="text-[10px] text-slate-400">
                              ID : {d.id}
                            </p>
                          </div>

                          <div className="md:text-left">
                            <p className="text-[11px] text-slate-700">
                              {formatDestination(data.destination)}
                            </p>
                            <p className="text-[10px] text-slate-400">
                              Nationalité : {data.country || "–"}
                            </p>
                          </div>

                          <div className="md:text-left">
                            <p className="text-[11px] text-slate-700">
                              {formatDate(d.created_at)}
                            </p>
                          </div>

                          <div className="md:text-right">
                            <span
                              className={`inline-flex items-center justify-end px-2 py-0.5 rounded-full text-[10px] ${meta.badgeClass}`}
                            >
                              {meta.label}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* ========== COLONNE DROITE : DÉTAIL ========= */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 h-[70vh] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-800">
                Détail de la demande
              </h2>
              {selectedDemande && (
                <StatusActions
                  demande={selectedDemande}
                  onChangeStatus={updateStatus}
                />
              )}
            </div>

            {!selectedDemande ? (
              <div className="flex-1 flex items-center justify-center text-xs text-slate-500">
                Sélectionnez une demande dans la liste à gauche.
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto pr-1 text-xs space-y-4">
                <DetailSection title="Récapitulatif">
                  <DetailRow label="ID demande" value={selectedDemande.id} />
                  <DetailRow
                    label="Créée le"
                    value={formatDate(selectedDemande.created_at)}
                  />
                  <DetailRow
                    label="Statut du dossier"
                    value={getDossierStatusLabel(selectedDemande.status)}
                  />
                  <DetailRow
                    label="Statut du paiement"
                    value={getPaymentStatusLabel(selectedDemande.status)}
                  />
                  <DetailRow
                    label="Destination"
                    value={formatDestination(selectedDemande.data?.destination)}
                  />
                  <DetailRow
                    label="Nationalité (landing)"
                    value={selectedDemande.data?.country || "–"}
                  />
                </DetailSection>

                <DetailSection title="Identité">
                  <DetailRow
                    label="Nom complet"
                    value={(`${selectedDemande.data?.prenoms || ""} ${selectedDemande.data?.nom || ""}`).trim() || "–"}
                  />
                  <DetailRow label="Genre" value={selectedDemande.data?.genre || "–"} />
                  <DetailRow label="Date de naissance" value={selectedDemande.data?.dateNaissance || "–"} />
                  <DetailRow label="Email" value={selectedDemande.data?.email || "–"} />
                </DetailSection>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

// ================== SOUS-COMPOSANTS UI ==================

function StatCard({ label, value, tone = "slate" }) {
  const toneMap = {
    slate: "bg-slate-50 text-slate-800 border-slate-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    rose: "bg-rose-50 text-rose-800 border-rose-100",
  };

  const toneClass = toneMap[tone] || toneMap.slate;

  return (
    <div className={`rounded-xl border px-3 py-2.5 flex flex-col justify-between ${toneClass}`}>
      <span className="text-[11px] font-medium opacity-80">{label}</span>
      <span className="text-lg font-bold leading-tight">{value}</span>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <section className="border border-slate-100 rounded-lg p-3 bg-slate-50/60">
      <h3 className="text-[11px] font-semibold text-slate-700 mb-2">{title}</h3>
      <dl className="space-y-1.5">{children}</dl>
    </section>
  );
}

function DetailRow({ label, value, multi = false }) {
  return (
    <div className="grid grid-cols-[0.9fr,1.1fr] gap-2 items-start">
      <dt className="text-[11px] text-slate-500">{label}</dt>
      <dd className={`text-[11px] text-slate-800 ${multi ? "whitespace-pre-wrap" : "truncate"}`}>
        {value || "–"}
      </dd>
    </div>
  );
}

function StatusActions({ demande, onChangeStatus }) {
  const current = demande.status || "";

  return (
    <div className="flex gap-1.5">
      <button
        type="button"
        onClick={() => onChangeStatus(demande.id, "payé")}
        className={`text-[11px] px-2.5 py-1 rounded-lg border ${
          current === "payé"
            ? "bg-amber-500 text-white border-amber-500"
            : "border-amber-300 text-amber-700 bg-amber-50"
        }`}
      >
        En attente
      </button>

      <button
        type="button"
        onClick={() => onChangeStatus(demande.id, "validee")}
        className={`text-[11px] px-2.5 py-1 rounded-lg border ${
          current === "validee"
            ? "bg-emerald-500 text-white border-emerald-500"
            : "border-emerald-300 text-emerald-700 bg-emerald-50"
        }`}
      >
        Valider
      </button>

      <button
        type="button"
        onClick={() => onChangeStatus(demande.id, "annulee")}
        className={`text-[11px] px-2.5 py-1 rounded-lg border ${
          current === "annulee"
            ? "bg-rose-500 text-white border-rose-500"
            : "border-rose-300 text-rose-700 bg-rose-50"
        }`}
      >
        Annuler
      </button>
    </div>
  );
}
